import React, { FC, PropsWithChildren } from 'react'
import { Box } from '@mui/material'
import { DataLoad, PropsInjector, SearchForm, TabGroupFilter } from 'src/components'
import { SensorColumnsDataGrid, ThingspaceSensorColumns } from 'src/components/columns/sensor'
import { SensorBillingColumns, ThingspaceBillingColumns } from 'src/components/columns/sensorBillingStats'
import { BillingSensorsFilters, defaultBillingEnvironment } from 'src/components/forms/sensor/billingSensorsFilters'
import { useSensorListRequest, useSensorTypesRequest } from 'src/hooks/api'
import { useThingspaceSensorsListRequest } from 'src/hooks/api/useThingspaceSensors'
import { useQueryParamsState } from 'src/hooks/ui'
import { SearchParamsKeys } from 'src/enums'
import { CommonSensorList } from '../sensorList/commonSensorsList'

const climitColumns = [
  SensorColumnsDataGrid.Id(),
  SensorColumnsDataGrid.IMEI,
  SensorColumnsDataGrid.ActivationStatus,
  SensorColumnsDataGrid.DateAdded,
  SensorBillingColumns.ActivationDate,
  SensorBillingColumns.LastConnectionDate,
  SensorBillingColumns.LastPing,
  SensorColumnsDataGrid.Label,
  SensorColumnsDataGrid.LastActiveContractor,
  SensorColumnsDataGrid.Project
]

const thingspaceColumns = [
  ThingspaceBillingColumns.Id,
  ThingspaceBillingColumns.IMEI,
  ThingspaceSensorColumns.ActivationStatus,
  ThingspaceBillingColumns.DateAdded,
  ThingspaceBillingColumns.ActivationDate,
  ThingspaceBillingColumns.LastConnectionDate,
  ThingspaceBillingColumns.LastPing,
  ThingspaceBillingColumns.Label,
  ThingspaceBillingColumns.Contractor,
  ThingspaceBillingColumns.Project
]

const ClimitBillingSensorsList: FC<{ typeId?: number }> = ({ typeId }) => {
  const queryParamsState = useQueryParamsState()

  const { data, error, isInitialLoading, isError } = useSensorListRequest({
    params: {
      ...queryParamsState,
      // Environment defaults to production until the user picks another option in the filter
      [SearchParamsKeys.Environment]: queryParamsState[SearchParamsKeys.Environment] || defaultBillingEnvironment,
      ...(typeId ? { typeId } : {})
    }
  })

  return (
    <DataLoad isLoading={false} isError={isError} errorMessage={error?.message}>
      <CommonSensorList
        columns={climitColumns}
        data={data?.rows || []}
        count={data?.count || 0}
        isLoading={isInitialLoading}
        exportedFileName={`${document.title}_climit_sensors`}
      />
    </DataLoad>
  )
}

const ThingspaceBillingSensorsList: FC = () => {
  const queryParamsState = useQueryParamsState()

  const { data, error, isInitialLoading, isError } = useThingspaceSensorsListRequest({
    params: { ...queryParamsState }
  })

  return (
    <DataLoad isLoading={false} isError={isError} errorMessage={error?.message}>
      <CommonSensorList
        columns={thingspaceColumns}
        data={data?.rows || []}
        count={data?.count || 0}
        isLoading={isInitialLoading}
        exportedFileName={`${document.title}_thingspace_sensors`}
      />
    </DataLoad>
  )
}

const SensorTypeLoader: FC<PropsWithChildren> = ({ children }) => {
  const { data, isInitialLoading, isError } = useSensorTypesRequest()
  const climitSensorType = data?.rows?.find((type) => type.name === 'jobsite_conditions_monitor')
  const typeId = climitSensorType?.id

  return (
    <DataLoad isLoading={isInitialLoading} isError={isError}>
      <PropsInjector props={{ typeId }}>{children}</PropsInjector>
    </DataLoad>
  )
}

const BillingSensorsTabs: FC<{ typeId?: number }> = ({ typeId }) => {
  return (
    <>
      <Box mb={2}>
        <SearchForm />
      </Box>
      <Box mb={2}>
        <BillingSensorsFilters />
      </Box>
      <TabGroupFilter
        name={SearchParamsKeys.Tab}
        paramsToRemove={[SearchParamsKeys.Page]}
        statusList={[
          { status: 'CLIMIT sensors', content: <ClimitBillingSensorsList typeId={typeId} /> },
          { status: 'ThingSpace sensors', content: <ThingspaceBillingSensorsList /> }
        ]}
      />
    </>
  )
}

export const SensorBillingSensorsTables: FC = () => {
  return (
    <SensorTypeLoader>
      <BillingSensorsTabs />
    </SensorTypeLoader>
  )
}
